import * as Sentry from "@sentry/browser";
import { ajax } from "lib/observable";
import { Observable } from "rxjs";
import "rxjs/add/operator/map";
import { MUTATE_OPERATION_ENHANCED, MUTATE_OPERATIONS_SENT, COLLECTIONS } from "constants/action-types";
import { fetchCollectionPOIs } from "actions/collections";
import siteConfig from "site-config";
import { empty } from "rxjs/observable/empty";
import { Map, List } from "immutable";
import { push } from "react-router-redux";
import { add as snackbarAdd } from "actions/snackbars";
import { formatMessage } from "lib/globalize-util";
import { getAndIncrementRequestId, mapToOperation } from "./helpers";
import {
    ROOT_NAMESPACE,
    ITEM_DISPLAY_PREFERENCES_NAMESPACE,
    ITEMS_NAMESPACE,
    ALLOCATION_DIMENSIONS_NAMESPACE,
    REVIEW_PLANS_NAMESPACE,
    DELIVERY_PURCHASE_ORDERS_NAMESPACE,
    REVIEW_DISPLAY_PREFERENCES_NAMESPACE,
    COLLECTION_POI_TAGS_NAMESPACE,
    DEFAULT_ITEM_NAMESPACE,
    EXPORT_VALIDATION_STATUS_NAMESPACE,
    ALLOCATION_BAG_NAMESPACE,
    MERCHANT_ITEM_DISPLAY_PREFERENCES_NAMESPACE,
    TARGET_GROUPS_NAMESPACE,
    TARGET_BAG_NAMESPACE,
    TARGETS_NAMESPACE,
} from "constants/product-collection";
import * as SentryUtil from "lib/sentry-util";
import { AssortmentTransactions, AssortmentEvents } from "constants/sentry-metrics";
import { BAG_VALUE_PARAMS_SEPARATOR, createBatchMutations } from "helpers/allocation-dimension";
import { amphibiousToJS } from "helpers/objUtils";
import { getCollectionAllocationBag } from "reducers/selectors/collection";
import { ITEM_DISPLAY_PROPERTY_KEY } from "./collection-item-display-property";
import { makeOverrideDisplayNamePayload } from "./helpers/makeOverrideDisplayNamePayload";
import { getAllocationBagMutationPayload } from "./helpers/getAllocationBagMutationPayload";

type Mutation = {
    key: string;
    operation: string;
    value: Map<string, any>;
};

const mapMapToOperation = (mutation: Mutation, prevSnapshot: Map<string, any>) => {
    let operationPayload;
    switch (mutation.operation) {
        case "set":
            if (mutation.key === ITEM_DISPLAY_PROPERTY_KEY) {
                operationPayload = makeOverrideDisplayNamePayload(
                    prevSnapshot,
                    mutation.value.getIn(["struct_value", "keys"]),
                );
            } else {
                const property = `${mutation.operation}_${mutation.key}`;
                const { value } = mutation;
                let finalValue;
                if (Map.isMap(value)) {
                    finalValue = value.toJS();
                    if (finalValue.struct_value) {
                        finalValue = finalValue.struct_value;
                    } else if (finalValue.string_value) {
                        finalValue = finalValue.string_value;
                    }
                }
                operationPayload = {
                    [property]: {
                        value,
                    },
                };
            }
            break;
        default:
    }

    if (!operationPayload) {
        throw new Error(`Operation ${mutation.operation} not implemented`);
    }
    return operationPayload;
};

export const collectionItemsSyncEpic = (action$, store) => {
    return (
        action$
            .ofType(COLLECTIONS.COLLECTION.ITEMS.SYNC.START)
            // Dont proceed if there is something sending or there is nothing to send
            .filter(({ payload: { objectId } }) => {
                const { mutate } = store.getState();
                const { sentMutations, uncommittedMutations } = mutate;
                const sentMutation = sentMutations.get(objectId);
                const uncommittedMutation = uncommittedMutations.get(objectId) || List();

                return !sentMutation && !!uncommittedMutation.size;
            })
            .map(({ payload }) => {
                const { collection, mutate } = store.getState();
                const { committedHEADs, uncommittedMutations } = mutate;
                const { sessionId, merchantId } = collection;
                const { objectId, syncData } = payload;

                const committedHEAD = committedHEADs.get(objectId);

                const operations = [];

                // TODO: NEW BAG. Remove this check when implementation is done
                const allocationBag = getCollectionAllocationBag(store.getState(), collection.collectionId);

                uncommittedMutations.getIn([objectId, 0]).operations.forEach((operation) => {
                    // TODO: NEW BAG. Remove this check when implementation is done
                    if (allocationBag) {
                        const rowValue = amphibiousToJS(operation.value);
                        const toCheck = [
                            "allocations",
                            "bulks",
                            "pack_allocations",
                            "po_creation_exports",
                            "inventory_units",
                        ];
                        const exists = toCheck.filter((item) => !!rowValue?.[item]);
                        if (exists.length > 0) {
                            Sentry.captureException(new Error("Unexpected old allocation bag detected"), {
                                extra: {
                                    bagTypes: exists,
                                },
                            });
                        }
                    }
                    operations.push(mapToOperation(operation));
                });

                // TODO: Measurement
                // console.timeEnd("Quantity changes - Bag version 0");

                const requestId = getAndIncrementRequestId(objectId);
                const revision = parseInt(committedHEAD.revision, 10) + 1;
                const command = {
                    operations,
                    revision,
                    req_id: requestId,
                    syncData: !!syncData,
                };

                store.dispatch({
                    type: MUTATE_OPERATIONS_SENT,
                    payload: {
                        requestId,
                        objectId,
                        revision,
                        objectType: committedHEAD.object_type,
                        sentMutationsIndex: 0,
                    },
                });

                return {
                    command,
                    sessionId,
                    requestId,
                    collectionId: committedHEAD.document_id,
                    objectId,
                    revision,
                    merchantId,
                };
            })
            .concatMap(({ command, collectionId, requestId, sessionId, objectId, merchantId }) => {
                SentryUtil.finishTransaction(AssortmentTransactions.Mutate);

                // Make it a queue, wait for response before continueing
                // TODO: Measurement
                // console.time("Saving request - bag version 0");
                const url = `/v1/collections/${collectionId}/ref/items/mutate?sid=${sessionId}&req_id=${requestId}`;
                return ajax({
                    body: { ...command, merchant_id: merchantId },
                    method: "POST",
                    url: siteConfig.goAPI + url,
                })
                    .map(() => {
                        // TODO: Measurement
                        // console.timeEnd("Saving request - bag version 0");
                        return {
                            payload: { collectionId },
                            type: COLLECTIONS.COLLECTION.ITEMS.SYNC.COMPLETED,
                        };
                    })
                    .catch((error) => {
                        const actions = [];

                        if (error.status === 409) {
                            return Observable.of({
                                type: COLLECTIONS.COLLECTION.ITEMS.SYNC.CONFLICT,
                                meta: {
                                    collectionId,
                                    objectId,
                                },
                                payload: error,
                                error: true,
                            });
                        } else if (error.status === 404) {
                            // attempting to modify a deleted collection
                            actions.push(Observable.of(push("/assortments")));
                            actions.push(
                                Observable.of(
                                    snackbarAdd({ message: formatMessage("This assortment is no longer available") }),
                                ),
                            );
                        } else {
                            actions.push(
                                Observable.of({
                                    type: COLLECTIONS.COLLECTION.ITEMS.SYNC.ERROR,
                                    payload: error,
                                }),
                            );
                        }

                        return Observable.merge(
                            ...actions,
                            Observable.of({
                                error: true,
                                payload: error,
                                type: COLLECTIONS.COLLECTION.ITEMS.SYNC.COMPLETED,
                            }),
                        );
                    });
            })
    );
};

export const collectionDefaultItemSync = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.DEFAULT_ITEM.SYNC.START)
        .filter(({ payload: { objectId } }) => {
            const { mutate } = store.getState();
            const { sentMutations, uncommittedMutations } = mutate;
            const sentMutation = sentMutations.get(objectId);
            const uncommittedMutation = uncommittedMutations.get(objectId) || List();

            return !sentMutation && !!uncommittedMutation.size;
        })
        .map(({ payload }) => {
            const { collection, mutate } = store.getState();
            const { committedHEADs, uncommittedMutations } = mutate;
            const { sessionId, merchantId } = collection;
            const { objectId } = payload;

            const committedHEAD = committedHEADs.get(objectId);

            const operations = [];

            uncommittedMutations.getIn([objectId, 0]).operations.forEach((operation) => {
                const { set } = mapToOperation(operation);
                if (set) {
                    operations.push({ set_fields: { ...set, value: set.value.struct_value } });
                }
            });

            const requestId = getAndIncrementRequestId(objectId);
            const revision = parseInt(committedHEAD.revision, 10) + 1;
            const command = {
                data: { operations, revision, req_id: requestId, merchant_id: merchantId },
                merchant_id: merchantId,
                req_id: requestId,
            };

            store.dispatch({
                type: MUTATE_OPERATIONS_SENT,
                payload: {
                    requestId,
                    objectId,
                    revision,
                    objectType: committedHEAD.object_type,
                    sentMutationsIndex: 0,
                },
            });

            return { command, sessionId, requestId, collectionId: committedHEAD.document_id, merchantId };
        })
        .concatMap(({ collectionId, requestId, sessionId, merchantId, command }) => {
            const url = `/v1/collections/${collectionId}/ref/default_item/mutate?sid=${sessionId}&req_id=${requestId}`;

            return ajax({
                body: { ...command, merchant_id: merchantId },
                method: "PUT",
                url: siteConfig.goAPI + url,
            }).map(() => ({ type: COLLECTIONS.COLLECTION.ITEMS.DEFAULT_ITEM.SYNC.COMPLETE }));
        });
};

export const collectionSyncEpic = (action$, store) => {
    return (
        action$
            .ofType(COLLECTIONS.COLLECTION.SYNC.START)
            // Dont proceed if there is something sending or there is nothing to send
            .filter(({ payload: { objectId } }) => {
                const { mutate } = store.getState();
                const { sentMutations, uncommittedMutations } = mutate;
                const sentMutation = sentMutations.get(objectId);
                const uncommittedMutation = uncommittedMutations.get(objectId) || List();
                return !sentMutation && !!uncommittedMutation.size;
            })
            .map(({ payload }) => {
                const { collection, mutate } = store.getState();
                const { committedHEADs, uncommittedMutations } = mutate;
                const { sessionId, merchantId } = collection;
                const { objectId } = payload;

                const committedHEAD = committedHEADs.get(objectId);

                const operations = []; // uncommittedMutations.get(objectId).map((mutation) => mapMapToOperation(mutation));
                uncommittedMutations.get(objectId).forEach((mutation) => {
                    mutation.operations.forEach((operation) => {
                        operations.push(mapMapToOperation(operation, mutation.oldSnapshot));
                    });
                });

                const requestId = getAndIncrementRequestId(objectId);
                const revision = parseInt(committedHEAD.revision, 10) + 1;
                const command = {
                    operations,
                    revision,
                    req_id: requestId,
                };

                store.dispatch({
                    type: MUTATE_OPERATIONS_SENT,
                    payload: {
                        requestId,
                        objectId,
                        revision,
                        objectType: committedHEAD.object_type,
                    },
                });

                return { command, sessionId, requestId, collectionId: committedHEAD.document_id, objectId, merchantId };
            })
            .concatMap(({ command, collectionId, requestId, sessionId, objectId, merchantId }) => {
                // Make it a queue, wait for response before continueing
                const url = `/v1/collections/${collectionId}/ref/properties/mutate?sid=${sessionId}&req_id=${requestId}`;
                return ajax({
                    body: { ...command, merchant_id: merchantId },
                    method: "POST",
                    url: siteConfig.goAPI + url,
                })
                    .map(() => ({ type: COLLECTIONS.COLLECTION.SYNC.COMPLETED }))
                    .catch((error) => {
                        const actions = [];

                        if (error.status === 409) {
                            return Observable.of({
                                type: COLLECTIONS.COLLECTION.SYNC.CONFLICT,
                                meta: {
                                    collectionId,
                                    objectId,
                                },
                                payload: error,
                                error: true,
                            });
                        } else if (error.status === 404) {
                            // attempting to modify a deleted collection
                            actions.push(Observable.of(push("/assortments")));
                            actions.push(
                                Observable.of(
                                    snackbarAdd({ message: formatMessage("This assortment is no longer available") }),
                                ),
                            );
                        } else {
                            actions.push(
                                Observable.of({
                                    type: COLLECTIONS.COLLECTION.SYNC.ERROR,
                                    payload: error,
                                }),
                            );
                        }

                        return Observable.merge(
                            ...actions,
                            Observable.of({
                                error: true,
                                payload: error,
                                type: COLLECTIONS.COLLECTION.SYNC.COMPLETED,
                            }),
                        );
                    });
            })
    );
};

export const collectionsSyncEpic = (action$, store) => {
    return action$
        .ofType(MUTATE_OPERATION_ENHANCED)
        .filter(
            ({
                payload: {
                    HEAD: { namespace },
                },
            }) => namespace === ROOT_NAMESPACE,
        )
        .concatMap(({ payload }) => {
            switch (payload.HEAD.object_type) {
                case ITEMS_NAMESPACE:
                    SentryUtil.finishSpan(AssortmentTransactions.Mutate, AssortmentEvents.MutateEnhanced);
                    return Observable.timer(1).map(() => ({
                        type: COLLECTIONS.COLLECTION.ITEMS.SYNC.START,
                        payload,
                    }));
                case ITEM_DISPLAY_PREFERENCES_NAMESPACE:
                    return Observable.timer(1).map(() => ({
                        type: COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.SYNC.START,
                        payload,
                    }));
                case MERCHANT_ITEM_DISPLAY_PREFERENCES_NAMESPACE:
                    return Observable.timer(1).map(() => ({
                        type: COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.SYNC.START,
                        payload,
                    }));
                case REVIEW_DISPLAY_PREFERENCES_NAMESPACE:
                    return Observable.timer(1).map(() => ({
                        type: COLLECTIONS.COLLECTION.ITEMS.REVIEW_TAB.SYNC.START,
                        payload,
                    }));
                case REVIEW_PLANS_NAMESPACE:
                    return Observable.timer(1).map(() => ({
                        type: COLLECTIONS.COLLECTION.REVIEW_PLANS.SYNC.START,
                        payload,
                    }));
                case ALLOCATION_DIMENSIONS_NAMESPACE:
                    return Observable.timer(1).map(() => ({
                        type: COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.SYNC.START,
                        payload,
                    }));
                case DELIVERY_PURCHASE_ORDERS_NAMESPACE:
                    return Observable.timer(1).map(() => ({
                        type: COLLECTIONS.COLLECTION.DELIVERY_PURCHASE_ORDER.SYNC.START,
                        payload,
                    }));
                case ROOT_NAMESPACE:
                    return Observable.timer(1).map(() => ({
                        type: COLLECTIONS.COLLECTION.SYNC.START,
                        payload,
                    }));
                case COLLECTION_POI_TAGS_NAMESPACE:
                    return Observable.timer(1).map(() => ({
                        type: COLLECTIONS.COLLECTION.POI.TAG.SYNC.START,
                        payload,
                    }));
                case DEFAULT_ITEM_NAMESPACE:
                    return Observable.timer(1).map(() => ({
                        type: COLLECTIONS.COLLECTION.ITEMS.DEFAULT_ITEM.SYNC.START,
                        payload,
                    }));
                case EXPORT_VALIDATION_STATUS_NAMESPACE:
                    return Observable.timer(1).map(() => ({
                        type: COLLECTIONS.COLLECTION.VALIDATION_LOG.SYNC.START,
                        payload,
                    }));
                case ALLOCATION_BAG_NAMESPACE:
                    SentryUtil.finishSpan(AssortmentTransactions.MutateAllocationBag, AssortmentEvents.MutateEnhanced);
                    return Observable.timer(1).map(() => ({
                        type: COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.BAG_VALUE.SYNC.START,
                        payload,
                    }));
                case TARGET_BAG_NAMESPACE:
                    return Observable.timer(1).map(() => ({
                        type: COLLECTIONS.COLLECTION.TARGET_METRICS.BAG_VALUE.SYNC.START,
                        payload,
                    }));
                case "nuorder.service.collection.saks_demo_brand_door_budgets":
                    return Observable.timer(1).map(() => ({
                        type: COLLECTIONS.COLLECTION_BUDGET.SYNC.START,
                        payload,
                    }));
                case TARGET_GROUPS_NAMESPACE:
                    return Observable.timer(1).map(() => ({
                        type: COLLECTIONS.COLLECTION.TARGET_METRICS.TARGET_GROUPS.SYNC.START,
                        payload,
                    }));
                case TARGETS_NAMESPACE:
                    return Observable.timer(1).map(() => ({
                        type: COLLECTIONS.COLLECTION.TARGET_METRICS.TARGETS.SYNC.START,
                        payload,
                    }));
                case "nuorder.service.collection.budget_calculation_results":
                default:
                    return empty();
            }
        });
};

export const createPOIEpic = (action$, store) => {
    return action$
        .ofType(MUTATE_OPERATION_ENHANCED)
        .filter(
            ({
                payload: {
                    HEAD: { namespace, object_type },
                },
            }) => namespace === ROOT_NAMESPACE && object_type != COLLECTION_POI_TAGS_NAMESPACE,
        )
        .switchMap(({ payload }) => {
            const { sessionId, merchantId } = store.getState().collection;
            return (
                Observable.timer(30000) // save snapshot if not changes after 30 seconds.
                    .takeUntil(action$.ofType(COLLECTIONS.COLLECTION.POI.TAG.SET.START)) // If we are saving captured bulks,
                    // we will not need to create a new POI entry, as epic tied to COLLECTIONS.COLLECTION.POI.TAG.SET.START will create one by itself
                    // or will reuse last one if there is any
                    .concatMap(() => {
                        const url = `/v1/collections/${payload.HEAD.document_id}/pois?sid=${sessionId}`;
                        return ajax({
                            body: {
                                merchant_id: merchantId,
                            },
                            method: "POST",
                            url: siteConfig.goAPI + url,
                        })
                            .catch((error) => empty())
                            .concatMap(() => [fetchCollectionPOIs(payload.HEAD.document_id)]);
                    })
            );
        });
};

export const collectionAllocationDimensionsBagValuesSync = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.BAG_VALUE.SYNC.START)
        .filter(({ payload: { objectId } }) => {
            const { mutate } = store.getState();
            const { sentMutations, uncommittedMutations } = mutate;
            const sentMutation = sentMutations.get(objectId);
            const uncommittedMutation = uncommittedMutations.get(objectId) || List();

            return !sentMutation && !!uncommittedMutation.size;
        })
        .map(({ payload }) => {
            const { collection, mutate } = store.getState();
            const { committedHEADs, uncommittedMutations } = mutate;
            const { sessionId, merchantId } = collection;
            const { objectId } = payload;

            const committedHEAD = committedHEADs.get(objectId);

            const operations = [];

            uncommittedMutations.getIn([objectId, 0]).operations.forEach((mutation) => {
                const { childKey, operation } = mutation;
                const [rowId] = childKey.split(BAG_VALUE_PARAMS_SEPARATOR);

                const valuePayload = getAllocationBagMutationPayload(mutation);
                operations.push({
                    [operation]: {
                        item_id: rowId,
                        value: valuePayload,
                    },
                });
            });

            // Show loading undo on the grid when it's big mutations are happening
            // This might not be best place to handle UI-related logic, like showing loading icon.
            // TODO: NEW ALLOCATION BAG Revisit how to show loading outside epic
            if (operations.length >= 1000) {
                collection.gridApi?.showLoadingOverlay();
            }

            const requestId = getAndIncrementRequestId(objectId);
            const revision = parseInt(committedHEAD.revision, 10) + 1;
            const command = {
                req_id: requestId,
                revision,
                // operations,
                batches: createBatchMutations(operations),
                merchant_id: merchantId,
            };

            store.dispatch({
                type: MUTATE_OPERATIONS_SENT,
                payload: {
                    requestId,
                    objectId,
                    revision,
                    objectType: committedHEAD.object_type,
                    sentMutationsIndex: 0,
                },
            });

            return { command, sessionId, requestId, collectionId: committedHEAD.document_id, objectId };
        })
        .concatMap(({ collectionId, requestId, sessionId, command, objectId }) => {
            if (command.batches.length === 0) {
                return [{ type: COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.BAG_VALUE.SYNC.COMPLETE }];
            }
            SentryUtil.finishTransaction(AssortmentTransactions.MutateAllocationBag);
            const url = `/v1/collections/${collectionId}/ref/items/allocation_bag/mutate?sid=${sessionId}&req_id=${requestId}`;

            return ajax({
                body: command,
                method: "POST",
                url: siteConfig.goAPI + url,
            })
                .map(() => {
                    return { type: COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.BAG_VALUE.SYNC.COMPLETE };
                })
                .catch((error) => {
                    const actions = [];

                    if (error.status === 409) {
                        return Observable.of({
                            type: COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.BAG_VALUE.SYNC.CONFLICT,
                            meta: {
                                collectionId,
                                objectId,
                            },
                            payload: error,
                            error: true,
                        });
                    }

                    actions.push(
                        Observable.of({
                            type: COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.BAG_VALUE.SYNC.ERROR,
                            payload: error,
                        }),
                        Observable.of({
                            error: true,
                            payload: error,
                            type: COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.BAG_VALUE.SYNC.COMPLETE,
                        }),
                    );

                    return Observable.merge(...actions);
                });
        });
};
